/** @jsxImportSource @emotion/react */
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import renderIf from '@signal/utils/renderIf'
import { UserDetailsType } from '../user/types'
import RootContext from './RootContext'
import {
  COMMON_UI_INSTRUMENTATION_ID,
  TELEM_EVENT_ENTERED_APP,
  TELEM_FUNC_COMMON_UI,
  TELEM_FUNC_NORMAL
} from './constants'
import injectGoogleAnalytics from './inject'
import { sendTelemetryPageView, sendTelemetryEvent } from './telemetry'
import { TelemetryPayloadType } from './types'

type InstrumentationRootPropsType = {
  appName: string
  instrumentationId?: string
  waitForPageInstrumentationFromApp?: boolean
  children: React.ReactNode
}

/**
 * @component InstrumentationRoot
 * The <InstrumentationRoot> React component, which holds global configuration for instrumentation
 */
export const InstrumentationRoot: React.FC<InstrumentationRootPropsType> = ({
  appName,
  instrumentationId,
  waitForPageInstrumentationFromApp,
  children
}) => {
  const [, setSentAppEntryTelemetry] = useState<boolean>(false)
  const [pagePayload, setPageInstrumentationPayload] = useState<
    TelemetryPayloadType
  >()

  const ensureAppEntryTelemetry = useCallback(
    (activeAccountId: string | undefined, userDetails: UserDetailsType) => {
      setSentAppEntryTelemetry(alreadySent => {
        if (!alreadySent) {
          sendTelemetryEvent({
            activeAccountId,
            appName,
            eventName: TELEM_EVENT_ENTERED_APP,
            pagePayload,
            telemetryFunc: TELEM_FUNC_COMMON_UI,
            userDetails
          })
        }
        return true
      })
    },
    [appName, pagePayload, setSentAppEntryTelemetry]
  )

  const sendPageViewTelemetry = useCallback(
    (activeAccountId: string | undefined, userDetails: UserDetailsType) => {
      sendTelemetryPageView({
        activeAccountId,
        appName,
        pagePayload,
        telemetryFunc: TELEM_FUNC_COMMON_UI,
        userDetails
      })
    },
    [appName, pagePayload]
  )

  const instrumentationRootContext = useMemo(
    () => ({
      appName,
      ensureAppEntryTelemetry,
      pagePayload,
      sendPageViewTelemetry,
      setPageInstrumentationPayload,
      waitingForPageInstrumentationFromApp: (waitForPageInstrumentationFromApp &&
        !pagePayload) as boolean
    }),
    [
      appName,
      ensureAppEntryTelemetry,
      pagePayload,
      sendPageViewTelemetry,
      setPageInstrumentationPayload,
      waitForPageInstrumentationFromApp
    ]
  )

  useEffect(() => {
    if (instrumentationId) {
      injectGoogleAnalytics(instrumentationId, TELEM_FUNC_NORMAL)
      injectGoogleAnalytics(COMMON_UI_INSTRUMENTATION_ID, TELEM_FUNC_COMMON_UI)
    }
  }, [instrumentationId])

  return renderIf(
    Boolean(instrumentationId),
    () => (
      <RootContext.Provider value={instrumentationRootContext}>
        {children}
      </RootContext.Provider>
    ),
    () => <>{children}</>
  ) as JSX.Element
}

export default memo(InstrumentationRoot)
